import { gql } from "@apollo/client"
import { countries } from "countries-list"
import { useEffect, useMemo } from "react"
import { useQuery } from "@oysterhr/core"
import { EquityIssueRiskTiers } from "~/app/utils/constants"
import {
  CountryIndex,
  EquityIssueHeatmapResponse,
  EquityIssueShape,
  UseEquityIssuesHeatmapProps,
} from "."

const QUERY_EQUITY_ISSUES_HEATMAP = gql`
  query EquityIssuesHeatmap(
    $countryCode: String
    $page: Int
    $perPage: Int
    $plans: [String!]
    $riskTiers: [Int!]
    $supported: Boolean
    $query: String
  ) {
    equityIssuesHeatmap(
      countryCode: $countryCode
      page: $page
      perPage: $perPage
      plans: $plans
      riskTiers: $riskTiers
      supported: $supported
      query: $query
    ) {
      countryCode
      sopRiskTier
      rsuRiskTier
      cshRiskTier
      supported
    }
  }
`

export function useEquityIssuesHeatmap({
  countryCode,
  page,
  perPage,
  plans = ["SOP", "RSU", "CSH"],
  riskTiers = EquityIssueRiskTiers,
  supported,
  query = "",
}: UseEquityIssuesHeatmapProps = {}) {
  const variables = useMemo(() => {
    return {
      countryCode,
      page,
      perPage,
      plans,
      riskTiers,
      supported,
      query,
    }
  }, [countryCode, page, perPage, plans, riskTiers, supported, query])

  const { data, loading, refetch } = useQuery<EquityIssueHeatmapResponse>(
    QUERY_EQUITY_ISSUES_HEATMAP,
    {
      variables,
    },
  )

  useEffect(() => {
    refetch(variables)
  }, [refetch, variables])

  const results = useMemo(() => {
    let transformedData: EquityIssueShape[] = []
    if (data) {
      transformedData = data.equityIssuesHeatmap.reduce((acc: EquityIssueShape[], equityIssue) => {
        const country = countries[equityIssue.countryCode as CountryIndex]
        acc.push({
          ...equityIssue,
          countryName: country.name,
          geographicRegion: country.continent,
        })
        return acc
      }, [])
    }
    return transformedData
  }, [data])

  return { results, loading }
}
